/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 照片筛选组件（重构版）
 * @LastEditors: yongqiang
 * @LastEditTime: 2026-04-22 11:31:32
 */
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useLayoutEffect
} from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Switch } from 'antd'
import {
  ArrowRightOutlined,
  BarChartOutlined,
  RightOutlined,
  SettingOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import {
  useWebSearchFilterHistory,
  useWebSearchKeywordFilterHistory
} from '../../../../app/hooks/useWebSearchFilterHistory'
import {
  buildWebSearchHistoryId,
  buildWebSearchHistoryText,
  buildWebSearchKeywordFilterHistoryId,
  buildWebSearchKeywordFilterHistoryText
} from '../../../components/PhotoFilter/utils/webSearchHistoryUtils'
import {
  PhotoFilterProps,
  QuickPickerItem,
  FilterState,
  RecommendSlotData
} from './types'
import { useFilterState } from '../../../components/PhotoFilter/hooks/useFilterState'
import { useFilterActions } from '../../../components/PhotoFilter/hooks/useFilterActions'
import { useFilterSubmit } from '../../../components/PhotoFilter/hooks/useFilterSubmit'
import {
  calcMemberPickedValue,
  calcWatermarkPickedValue,
  calcMoreFilterCount,
  calcTagFilterCount,
  isTimeFilterActive,
  isMemberFilterActive,
  isWatermarkFilterActive,
  getInitialFilter
} from './utils/filterUtils'
import {
  trackFilterTimeClick,
  trackPicsPageClick
} from '../../../components/PhotoFilter/utils/sensorUtils'
import { usePhotoLabelEnabled } from '../../../../app/hooks/usePhotoLabelEnabled'
import { useIsAdmin } from '../../../../app/hooks'
import KeywordsSearch from '../../../components/PhotoFilter/components/inputs/KeywordsSearch'
import TimeSelector from '../../../components/PhotoFilter/components/selectors/TimeSelector'
import MemberSelector from '../../../components/PhotoFilter/components/selectors/MemberSelector'
import WatermarkSelector from '../../../components/PhotoFilter/components/selectors/WatermarkSelector'
import MoreFilterSelector from '../../../components/PhotoFilter/components/selectors/MoreFilterSelector'
import TagFilterSelector from '../../../components/PhotoFilter/components/selectors/TagFilterSelector'
import QuickFilterList from '../../../components/PhotoFilter/components/recommend/QuickFilterList'
import RecommendKeywords from '../../../components/PhotoFilter/components/recommend/RecommendKeywords'
import PhotoTagManageModal from '../../../components/PhotoTagManageModal'
import PhotoTagTeamStatsModal from '../../../components/PhotoTagTeamStatsModal'
import BuildingSafetyEntry from './components/entries/BuildingSafetyEntry'
import {
  fetchPhotoLabelStatsByMember,
  fetchTeamTags
} from '../../../photoTag/api'
import {
  trackPicspageClick,
  trackPicspageDialogShow,
  trackPicspageDialogClick,
  TagEntryFrom
} from '../../../photoTag/utils/track'
import { selectPhotosNum } from '../../../../slices/photosByContentSlice'
import { formatNum } from '../../../../utils/utils'
import DatesPicker from '../../../components/DatesPickerV2'
import DepartmentMemberPicker from '../../../components/DepartmentMemberPickerV2'
import WatermarksPicker from '../../../components/WatermarksPicker'
import LocationPicker from '../../../components/LocationPicker'
import TagPicker from '../../../components/TagPicker'
import { fetchWatermarkList } from '../../../../actions/photo'
import { setPaymentVisible } from '../../../../actions/main'
import CONFIG from '../../../../constants/config'
import { useVip } from '../../../../app/hooks'
import {
  isB8059TrackContextReady,
  trackWorkgroupB8059Click
} from '../../../components/PhotoListAggregateEntries/track'
import './styles/index.scss'
// keywords输入框的内容
let keywordsValue: string[] = []
// 快速日期选择项
const quickPickerItems: QuickPickerItem[] = [
  {
    text: '今天',
    clickItem: 'today',
    startTime: () => dayjs().startOf('day'),
    endTime: () => dayjs().endOf('day')
  },
  {
    text: '昨天',
    clickItem: 'yesterday',
    startTime: () => dayjs().add(-1, 'day').startOf('day'),
    endTime: () => dayjs().add(-1, 'day').endOf('day')
  },
  {
    text: '过去30天',
    clickItem: 'past30days',
    startTime: () => dayjs().add(-30, 'days').startOf('day'),
    endTime: () => dayjs().add(-1, 'days').endOf('day')
  },
  {
    text: '本月',
    clickItem: 'currentMonth',
    startTime: () => dayjs().startOf('month'),
    endTime: () => dayjs().endOf('day')
  },
  {
    text: '过去6个月',
    clickItem: 'pastSixMonths',
    startTime: () => dayjs().subtract(6, 'months').startOf('day'),
    endTime: () => dayjs().endOf('day')
  }
]

/**
 * 照片筛选组件
 */
const PhotoFilter: React.FC<PhotoFilterProps> = ({
  groupId,
  onSubmit,
  onReset,
  formPlace,
  extraKeywordAction,
  groupByUserRowExtra,
  keywordsRowAfter,
  hideQuickRow,
  fromPage
}) => {
  const moreFilterDropdown = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()
  const [recommendSlotData, setRecommendSlotData] =
    useState<RecommendSlotData | null>(null)
  const [keywordsResetToken, setKeywordsResetToken] = useState(0)

  // 测量"重置"那行(.pfv2-row--selectors)从 row left 到「重置」span 的 right 的距离,
  // 用作 keywords 行的 max-width,让 AI 入口按钮右边缘正好对齐"重置"按钮右边缘。
  // 不能直接用 row.getBoundingClientRect().width —— 那是 100% 父宽,不是内容宽。
  const [selectorsRowWidth, setSelectorsRowWidth] = useState<
    number | undefined
  >(undefined)
  useLayoutEffect(() => {
    const el = moreFilterDropdown.current
    if (!el) return

    const update = () => {
      const last = el.lastElementChild
      if (!last) return
      const rowLeft = el.getBoundingClientRect().left
      const lastRight = last.getBoundingClientRect().right
      const contentWidth = lastRight - rowLeft
      if (contentWidth > 0) {
        setSelectorsRowWidth(contentWidth)
      }
    }

    update()

    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(update)
    observer.observe(el)
    Array.from(el.children).forEach((child) => observer.observe(child))
    return () => observer.disconnect()
  }, [])

  // 从 Redux 获取数据
  const pickerUserCount = useSelector(
    (state: any) => state.organization?.pickerOrgUserCount || 0
  )
  const allWatermarksNum = useSelector(
    (state: any) => state.photo?.allWatermarksNum || 0
  )
  const members = useSelector((state: any) => state.group?.members)
  const myRole = useSelector((state: any) => state.group?.myRole)
  const industryCode = useSelector((state: any) => state.group?.industryCode)
  const { isSVip } = useVip()
  const inviteId = useSelector((state: any) => state.group?.invite_id)
  // 按人员功能白名单团队，无需SVIP即可使用
  const groupByUserWhitelist = ['461965381']
  const isGroupByUserAllowed =
    isSVip || groupByUserWhitelist.includes(String(inviteId))
  const { addHistory } = useWebSearchFilterHistory()
  const { addHistory: addKeywordFilterHistory } =
    useWebSearchKeywordFilterHistory()
  const webWatermarkSwitch = useSelector(
    (state: any) => state.main?.config?.web_watermark_switch === 'true'
  )
  const photoLabelEnabled = usePhotoLabelEnabled()
  const isAdmin = useIsAdmin()
  const userID = useSelector((state: any) => state.main?.user?.user_id) as
    | string
    | undefined
  const filteredPhotosNum = useSelector(selectPhotosNum)

  // fromPage 由父页面直接以 prop 传入 (ai_photo_king),用于照片标签入口曝光/点击埋点。
  // 之前从 useBatchTagMode().isProviderMounted 取开关,但 PhotoFilter 渲染在 BatchTagModeProvider
  // 之外 → isProviderMounted 恒 false,入口曝光/点击埋点被全部跳过(自 v4 上线起从未上报)。
  // 改为 prop 驱动:传了 fromPage 即上报;不传(非打标页复用)则跳过。
  // 默认值 'ai_photo_king' 仅占位,tagTrackEnabled 为 false 时不会真正发送。
  const fromPageForTrack = fromPage || 'ai_photo_king'
  const tagTrackEnabled = Boolean(fromPage)
  const openTagManageWithTrack = useCallback(
    (
      clickItem:
        | 'header_open_manage'
        | 'tag_entry_guide_click'
        | 'tag_filter_empty_cta',
      source: TagEntryFrom
    ) => {
      if (tagTrackEnabled) {
        trackPicspageClick({
          clickItem,
          fromPage: fromPageForTrack,
          elementFrom: source
        })
      }
      setManageSource(source)
      setTagManageModalVisible(true)
    },
    [fromPageForTrack, tagTrackEnabled]
  )

  // 管理标签 / 团队打标统计 弹窗（仅管理员可见入口）
  const [tagManageModalVisible, setTagManageModalVisible] = useState(false)
  const [teamStatsModalVisible, setTeamStatsModalVisible] = useState(false)
  // 团队标签数量：null = 加载中 / 不需要展示，0 = 空态展示限免 banner，>0 = 已配置展示常规入口
  const [teamTagCount, setTeamTagCount] = useState<number | null>(null)
  // smart-row 上「团队已打标 X 张」的数字；null = 未拉到（显示「—」）
  const [teamTaggedNum, setTeamTaggedNum] = useState<number | null>(null)
  // v4: 本次打开管理弹窗的来源入口（引导/管理/其他），作为 enter 的 elementFrom
  const [manageSource, setManageSource] = useState<TagEntryFrom>('other')
  // v4: 入口曝光去重，同一次挂载只上报一次
  const tagEntryExposedRef = useRef(false)

  const reloadTeamTagCount = useCallback(() => {
    if (!photoLabelEnabled || !isAdmin || !groupId || !userID) return
    fetchTeamTags(String(groupId), userID)
      .then((tags) => setTeamTagCount(tags.length))
      .catch(() => {
        // 拉取失败时保持 null（不展示），避免误显空态导致 CTA 误导
      })
  }, [photoLabelEnabled, isAdmin, groupId, userID])

  const reloadTeamTaggedNum = useCallback(() => {
    if (!photoLabelEnabled || !isAdmin || !groupId) return
    fetchPhotoLabelStatsByMember(String(groupId), 7)
      .then((res) => setTeamTaggedNum(res.total.tagged))
      .catch(() => {
        // 失败保持 null → 显示「—」，不打 toast 避免侵入性
      })
  }, [photoLabelEnabled, isAdmin, groupId])

  useEffect(() => {
    reloadTeamTagCount()
    reloadTeamTaggedNum()
  }, [reloadTeamTagCount, reloadTeamTaggedNum])

  // v4: 入口曝光埋点（区分引导/管理），smart-row 首次可见时上报一次
  useEffect(() => {
    if (!photoLabelEnabled || !isAdmin || teamTagCount === null) return
    if (!tagTrackEnabled || tagEntryExposedRef.current) return
    tagEntryExposedRef.current = true
    if (teamTagCount === 0) {
      trackPicspageDialogShow({
        popTitle: 'tag_entry_guide',
        fromPage: fromPageForTrack,
        number1: filteredPhotosNum,
        elementFrom: 'tag_entry_guide'
      })
    } else {
      trackPicspageDialogShow({
        popTitle: 'tag_entry_manage',
        fromPage: fromPageForTrack,
        number1: teamTaggedNum ?? undefined,
        elementFrom: 'tag_entry_manage'
      })
    }
  }, [
    photoLabelEnabled,
    isAdmin,
    teamTagCount,
    teamTaggedNum,
    tagTrackEnabled,
    fromPageForTrack,
    filteredPhotosNum
  ])

  // 使用自定义 Hooks
  const {
    filter,
    queryFilter,
    updateFilter,
    resetFilter: resetFilterState,
    updateQueryFilter,
    setLastPageCond,
    setIsAscSort
  } = useFilterState()

  const {
    showDatePickerPane,
    showMemberPickerModal,
    showWatermarkModal,
    showLocationPickerModal,
    showTagPickerModal,
    toggleFilterModal,
    closeAllModals,
    handleDatesChange: handleDatesChangeAction,
    handleMemberChange: handleMemberChangeAction,
    handleWatermarkChange: handleWatermarkChangeAction,
    handleLocationChange: handleLocationChangeAction,
    handleTagChange: handleTagChangeAction
  } = useFilterActions()

  // 存储最新的照片标签名称，供 executeSubmit 里的埋点使用
  const labelNameRef = useRef<string>('')

  const {
    handleReset: handleResetInternal,
    handleSubmit: handleSubmitInternal,
    prepareFilterForSubmit
  } = useFilterSubmit({
    groupId,
    myRole,
    members,
    allWatermarksNum,
    getLabelName: useCallback(() => labelNameRef.current, []),
    onSubmit: async (preparedFilter) => {
      const result = await onSubmit(preparedFilter)

      // webSearchHistory（组合条件历史）保存逻辑
      {
        const total = (result as any)?.total
        const isSearchResultValid = typeof total === 'number' && total > 0

        const isKeywordsEmpty =
          Array.isArray(preparedFilter.searchKeywords) &&
          preparedFilter.searchKeywords.length === 0 &&
          !preparedFilter.keyword

        const isMemberNonDefault = isMemberFilterActive(
          preparedFilter,
          pickerUserCount
        )

        const isTimeNonDefault = isTimeFilterActive(preparedFilter)

        if (
          isSearchResultValid &&
          isKeywordsEmpty &&
          isMemberNonDefault &&
          isTimeNonDefault
        ) {
          const text = buildWebSearchHistoryText(preparedFilter)
          const id = buildWebSearchHistoryId(preparedFilter)
          addHistory({ id, text, isSessionGenerated: true })
        }
      }

      // keywordHistory（关键词+筛选组合历史）保存逻辑
      {
        const total = (result as any)?.total
        const isSearchResultValid = typeof total === 'number' && total > 0

        const isKeywordsNotEmpty =
          Array.isArray(preparedFilter.searchKeywords) &&
          preparedFilter.searchKeywords.length > 0

        const isMemberNonDefault = isMemberFilterActive(
          preparedFilter,
          pickerUserCount
        )

        const isTimeNonDefault = isTimeFilterActive(preparedFilter)

        const isMemberOrTimeNonDefault = isMemberNonDefault || isTimeNonDefault

        if (
          isSearchResultValid &&
          isKeywordsNotEmpty &&
          isMemberOrTimeNonDefault
        ) {
          const text = buildWebSearchKeywordFilterHistoryText(preparedFilter)
          const id = buildWebSearchKeywordFilterHistoryId(preparedFilter)
          addKeywordFilterHistory({ id, text, isSessionGenerated: true })
        }
      }

      return result
    },
    onReset: async () => {
      resetFilterState()
      if (onReset) {
        await onReset()
      }
    },
    updateFilter,
    updateQueryFilter,
    setLastPageCond,
    setIsAscSort,
    closeAllModals,
    pageType: formPlace || 'allPhoto',
    formPlace,
    getInputKeywords: () => keywordsValue as string[]
  })

  const handleSensorOfClickEvent = (
    clickItem: string,
    restProperties: Record<string, any> = {}
  ) => {
    trackPicsPageClick({
      clickItem,
      isOpenErrorCorrection: queryFilter?.useSynonym,
      ...restProperties
    })
  }

  // 引导词所需内容
  const buildAiGuideStructured = () => {
    const departments = filter.departments || []
    const departmentUsers = filter.departmentUsers || []
    const totalMembersCount =
      (departmentUsers && departmentUsers.length) ||
      (departments && departments.length) ||
      0
    let time: { start?: any; end?: any } | undefined = undefined
    if (filter.times && filter.times.length === 2) {
      time = { start: filter.times[0], end: filter.times[1] }
    }
    // 如果是默认筛选则返回null
    const isDefaultRecentSixMonths =
      (!departments || departments.length === 0) &&
      (!departmentUsers || departmentUsers.length === 0) &&
      time &&
      time.start &&
      time.end &&
      time.start.isSame(
        dayjs().subtract(6, 'months').startOf('day'),
        'second'
      ) &&
      time.end.isSame(dayjs().endOf('day'), 'second')

    if (isDefaultRecentSixMonths) {
      return null
    }

    // 没有部门成员还有时间也返回null
    if (
      (!departments || departments.length === 0) &&
      (!departmentUsers || departmentUsers.length === 0) &&
      !time
    ) {
      return null
    }

    return {
      departments,
      departmentUsers,
      totalMembersCount,
      time
    }
  }

  const handleReset = async () => {
    setKeywordsResetToken((token) => token + 1)
    handleSensorOfClickEvent('reset')
    keywordsValue = [] // 清空全局变量，防止快速搜索时带上之前的关键词
    labelNameRef.current = '' // 清空标签名，重置后埋点不再携带旧标签
    await handleResetInternal()
  }

  const getSubmitKeywords = () => {
    if (filter.isNlpFilter) {
      const queryFilterKeywords =
        queryFilter && Array.isArray(queryFilter.searchKeywords)
          ? (queryFilter.searchKeywords as string[])
          : []
      return queryFilterKeywords
    }
    // 否则使用输入框的值（用户输入但未搜索的内容）
    return keywordsValue as string[]
  }

  // 处理日期变化（修改后自动触发搜索）
  const handleDatesChange = (startDate: any, endDate: any) => {
    handleDatesChangeAction(startDate, endDate, updateFilter)

    const orderedTimes =
      startDate && endDate && startDate.isAfter(endDate)
        ? [endDate, startDate]
        : [startDate, endDate]
    handleSubmit(
      getSubmitKeywords(),
      { times: orderedTimes } as Partial<FilterState>,
      { preserveInput: true }
    )
  }

  // 处理成员变化（修改后自动触发搜索）
  const handleMemberChange = (params: {
    selectedDepartmentUsers: any[]
    selectedDepartments: any[]
  }) => {
    handleMemberChangeAction(params, updateFilter)

    const overrideFilter: Partial<FilterState> = {
      departmentUsers: params.selectedDepartmentUsers,
      departments: params.selectedDepartments
    }

    handleSubmit(getSubmitKeywords(), overrideFilter, {
      preserveInput: true
    })
  }

  // 处理水印变化（修改后自动触发搜索）
  const handleWatermarkChange = (checkedList: string[]) => {
    handleWatermarkChangeAction(checkedList, updateFilter)

    const overrideFilter: Partial<FilterState> = {
      watermarks: checkedList
    }

    handleSubmit(getSubmitKeywords(), overrideFilter, {
      preserveInput: true
    })
  }

  // 处理地点变化（修改后自动触发搜索）
  const handleLocationChange = (location: {
    name: string
    detail: string
    type: number
  }) => {
    handleLocationChangeAction(location, updateFilter)

    const overrideFilter: Partial<FilterState> = {
      locations: [location]
    }

    handleSubmit(getSubmitKeywords(), overrideFilter, {
      preserveInput: true
    })
  }

  // 处理标签变化
  const handleTagChange = (
    labelIDs: string[],
    labelMatchAll: boolean,
    checkedNames?: string[]
  ) => {
    handleTagChangeAction(labelIDs, labelMatchAll, updateFilter)

    // 更新标签名 ref，供 executeSubmit 里的埋点读取
    labelNameRef.current = checkedNames ? checkedNames.join('|') : ''

    const overrideFilter: Partial<FilterState> = {
      labelIDs,
      labelMatchAll
    }

    handleSubmit(getSubmitKeywords(), overrideFilter, {
      preserveInput: true
    })
  }

  // 处理筛选提交；NLP 搜索后切换其他筛选条件时，需保持输入框显示用户原输入而非 NLP 返回关键词（见 isNlpFilter 相关分支）
  const handleSubmit = async (
    latestSearchKeywords?: string[],
    overrideFilter?: Partial<FilterState>,
    options?: { preserveInput?: boolean }
  ) => {
    // 构建用于搜索的筛选条件
    const mergedFilter = overrideFilter
      ? { ...filter, ...overrideFilter }
      : filter
    const filterToUse = latestSearchKeywords
      ? { ...mergedFilter, searchKeywords: latestSearchKeywords }
      : mergedFilter

    // 更新 Redux 状态（用于回显），需要区分场景避免错误更新 filter.searchKeywords
    if (overrideFilter || latestSearchKeywords) {
      const isNlpOverride = overrideFilter?.isNlpFilter
      const updateData: Partial<FilterState> = {}
      if (overrideFilter) {
        Object.assign(updateData, overrideFilter)
        if (isNlpOverride) {
          delete updateData.searchKeywords
        }
      }

      // 判断是否是 NLP 搜索后的切换筛选条件，如果是则不更新 filter.searchKeywords
      const isNlpFilterActive = filter.isNlpFilter
      const queryFilterKeywords =
        queryFilter && Array.isArray(queryFilter.searchKeywords)
          ? (queryFilter.searchKeywords as string[])
          : null
      const isUsingNlpKeywords =
        isNlpFilterActive &&
        queryFilterKeywords &&
        latestSearchKeywords &&
        JSON.stringify(latestSearchKeywords) ===
          JSON.stringify(queryFilterKeywords)

      // 如果preserveInput是true，不把搜索关键词写回 filter.searchKeywords，在hooks中处理
      if (options?.preserveInput) {
        if ('searchKeywords' in updateData) {
          delete updateData.searchKeywords
        }
        if (Object.keys(updateData).length > 0) {
          updateFilter(updateData)
        }
      } else {
        // 如果不是 NLP 搜索后的切换，更新 filter.searchKeywords
        if (!isNlpOverride && latestSearchKeywords && !isUsingNlpKeywords) {
          updateData.searchKeywords = latestSearchKeywords
        }
        if (Object.keys(updateData).length > 0) {
          updateFilter(updateData)
        }
      }
    }

    const preparedFilter = prepareFilterForSubmit(filterToUse)
    await handleSubmitInternal(preparedFilter)
  }

  // 渲染关键词区域
  const renderKeywordsArea = () => {
    const enableTagsDropdown = filter.useSuggestedSearch !== false

    return (
      <div
        className="pfv2-row pfv2-row--keywords"
        style={selectorsRowWidth ? { maxWidth: selectorsRowWidth } : undefined}
      >
        <div className="pfv2-block pfv2-keywords-block">
          <div className="pfv2-keywords-input has-search-button">
            <div className="pfv2-keywords">
              <KeywordsSearch
                value={filter.searchKeywords}
                onChange={(value) => {
                  keywordsValue = value
                  updateFilter({ searchKeywords: value })
                }}
                onInputChange={(text: string) => {
                  keywordsValue = text ? [text] : []
                }}
                onSubmit={handleSubmit}
                extraAction={extraKeywordAction}
                onInputFocusChange={(isFocused) => {
                  if (isFocused && filter.useSuggestedSearch === false) {
                    updateFilter({ useSuggestedSearch: true })
                  }
                }}
                recommendPlacement="external"
                onRecommendChange={(payload) => {
                  if (!payload || payload.keywords.length === 0) {
                    setRecommendSlotData(null)
                    return
                  }
                  setRecommendSlotData(payload)
                }}
                onSensorOfClickEvent={handleSensorOfClickEvent}
                showLabel={false}
                showTagsDropdown={enableTagsDropdown}
                resetToken={keywordsResetToken}
                aiGuideStructured={buildAiGuideStructured()}
              />
            </div>
          </div>
        </div>
        {keywordsRowAfter}
      </div>
    )
  }

  const filterMoreNum = calcMoreFilterCount(filter)

  const startTime = filter.times?.[0]
  const endTime = filter.times?.[1]
  const hasValidTime = Boolean(startTime && endTime)

  const isThisMonth =
    hasValidTime &&
    dayjs().startOf('month').isSame(startTime, 'second') &&
    dayjs().endOf('day').isSame(endTime, 'second')
  const isLastSixMonths =
    hasValidTime &&
    dayjs().subtract(6, 'months').startOf('day').isSame(startTime, 'day') &&
    dayjs().endOf('day').isSame(endTime, 'day')

  // 判断是否显示年份：如果开始和结束都在本年，不显示年份；否则显示年份
  const currentYear = dayjs().year()
  const startYear = hasValidTime ? startTime!.year() : null
  const endYear = hasValidTime ? endTime!.year() : null
  const showYear =
    hasValidTime && !(startYear === currentYear && endYear === currentYear)

  const startTimeFormated = hasValidTime
    ? showYear
      ? startTime!.format('YY.MM.DD')
      : startTime!.format('M.D')
    : ''
  const endTimeFormated = hasValidTime
    ? showYear
      ? endTime!.format('YY.MM.DD')
      : endTime!.format('M.D')
    : ''

  return (
    <div className="photo-filter-v2">
      <div className="pfv2-panel">
        {renderKeywordsArea()}

        <div className="pfv2-row pfv2-row--selectors" ref={moreFilterDropdown}>
          <TimeSelector
            hasValidTime={hasValidTime}
            isThisMonth={isThisMonth}
            isLastSixMonths={isLastSixMonths}
            startTimeLabel={startTimeFormated}
            endTimeLabel={endTimeFormated}
            onClick={() => {
              toggleFilterModal('time')
              trackFilterTimeClick()
              handleSensorOfClickEvent('choosePhotoDate')
            }}
            isActive={isTimeFilterActive(filter)}
          />

          <MemberSelector
            displayValue={calcMemberPickedValue(filter, pickerUserCount)}
            onClick={() => {
              toggleFilterModal('member')
              handleSensorOfClickEvent('choosePhotoMember')
            }}
            isActive={isMemberFilterActive(filter, pickerUserCount)}
          />

          <WatermarkSelector
            displayValue={calcWatermarkPickedValue(filter, allWatermarksNum)}
            onClick={() => {
              toggleFilterModal('watermark')
              handleSensorOfClickEvent('chooseWatermark')
            }}
            isActive={isWatermarkFilterActive(filter, allWatermarksNum)}
          />

          {photoLabelEnabled && (
            <TagFilterSelector
              filter={filter}
              count={calcTagFilterCount(filter)}
              teamTagCount={teamTagCount}
              filteredPhotosNum={filteredPhotosNum}
              isAdmin={isAdmin}
              onOpen={() => {
                handleSensorOfClickEvent('点击筛选标签')
                if (tagTrackEnabled) {
                  trackPicspageClick({
                    clickItem: 'tag_filter_open',
                    fromPage: fromPageForTrack
                  })
                  trackPicspageDialogShow({
                    popTitle: 'tag_filter_picker',
                    fromPage: fromPageForTrack
                  })
                }
                toggleFilterModal('tag')
              }}
              onSetupTags={() =>
                openTagManageWithTrack(
                  'tag_filter_empty_cta',
                  'tag_filter_empty_cta'
                )
              }
            />
          )}

          <MoreFilterSelector
            filter={filter}
            filterCount={filterMoreNum}
            webWatermarkSwitch={webWatermarkSwitch}
            onOpenLocationModal={() => {
              toggleFilterModal('location')
              handleSensorOfClickEvent('chooseLocation')
            }}
            onChangeFilter={(overrideFilter) => {
              updateFilter(overrideFilter)
              handleSubmit(undefined, overrideFilter, { preserveInput: true })
            }}
            getPopupContainer={() =>
              moreFilterDropdown.current || document.body
            }
            onSensorOfClickEvent={(clickItem, restProperties) => {
              handleSensorOfClickEvent(clickItem, restProperties)
            }}
          />

          <span className="pfv2-reset-link" onClick={handleReset}>
            重置
          </span>
        </div>

        {/* 管理标签 / 团队打标统计 — 仅命中标签 KV 的团队 + 管理员可见 (master) */}
        {photoLabelEnabled && isAdmin && teamTagCount !== null && (
          <div className="pfv2-row pfv2-row--smart">
            <span
              className="pfv2-smart-link"
              onClick={() =>
                openTagManageWithTrack(
                  'header_open_manage',
                  'header_open_manage'
                )
              }
            >
              <SettingOutlined /> 管理标签
            </span>
            {teamTagCount === 0 ? (
              <span
                className="pfv2-smart-cta"
                onClick={() =>
                  openTagManageWithTrack(
                    'tag_entry_guide_click',
                    'tag_entry_guide'
                  )
                }
              >
                <span className="pfv2-smart-cta__badge">✦ 限免</span>
                <span className="pfv2-smart-cta__text">
                  设置团队标签 → 让 <b>{formatNum(filteredPhotosNum)}</b>{' '}
                  张照片自动归类，查图效率 <em>提升 20 倍</em>
                </span>
                <ArrowRightOutlined className="pfv2-smart-cta__arrow" />
              </span>
            ) : (
              <span
                className="pfv2-smart-stats"
                onClick={() => {
                  if (tagTrackEnabled) {
                    trackPicspageClick({
                      clickItem: 'tag_entry_manage_stats',
                      fromPage: fromPageForTrack,
                      number1: teamTaggedNum ?? undefined
                    })
                  }
                  setTeamStatsModalVisible(true)
                }}
              >
                <BarChartOutlined /> 团队已打标{' '}
                <b>{teamTaggedNum === null ? '—' : formatNum(teamTaggedNum)}</b>{' '}
                张<span className="pfv2-smart-stats__when">· 近 7 天</span>
                <RightOutlined className="pfv2-smart-stats__arrow" />
              </span>
            )}
          </div>
        )}

        {!hideQuickRow && (
          <div className="pfv2-row pfv2-row--quick">
            <QuickFilterList
              items={quickPickerItems}
              disabled={filter.groupByUser}
              onSelect={(start, end, item) => {
                handleDatesChange(start, end)
                handleSensorOfClickEvent('quickChooseTime', {
                  clickQuickTimeContent: item.text
                })
              }}
            />

            <RecommendKeywords recommendSlotData={recommendSlotData} />

            <span className="pfv2-divider pfv2-group-by-user-divider">|</span>
            <div className="pfv2-group-by-user">
              <span className="pfv2-group-by-user-label">按人员</span>
              {/* {!isGroupByUserAllowed && (
              <span className="pfv2-group-by-user-badge">SVIP</span>
            )} */}
              <Switch
                size="small"
                checked={filter.groupByUser}
                onChange={(checked) => {
                  if (isB8059TrackContextReady()) {
                    trackWorkgroupB8059Click({
                      clickItem: checked
                        ? 'openGroupByUser'
                        : 'closeGroupByUser',
                      popType: '按人员'
                    })
                  }
                  if (checked && !isGroupByUserAllowed) {
                    // 非SVIP用户打开时，弹出付费弹窗定位到SVIP
                    dispatch(
                      setPaymentVisible(
                        true,
                        'webAllPhotoGroupByUser',
                        CONFIG.VIP_KIND.SVIP
                      )
                    )
                    return
                  }
                  if (checked) {
                    const start = dayjs().startOf('day')
                    const end = dayjs().endOf('day')
                    handleSubmit(
                      getSubmitKeywords(),
                      { groupByUser: true, times: [start, end] },
                      { preserveInput: true }
                    )
                  } else {
                    const { times: defaultTimes } = getInitialFilter()
                    handleSubmit(
                      getSubmitKeywords(),
                      { groupByUser: false, times: defaultTimes! },
                      { preserveInput: true }
                    )
                  }
                }}
              />
            </div>
            {groupByUserRowExtra && (
              <div className="pfv2-ai-photo-jun-entry">
                {groupByUserRowExtra}
              </div>
            )}
          </div>
        )}
      </div>

      <DatesPicker
        isAllPhoto={true}
        visible={showDatePickerPane}
        // dates 兜底:filter.times 可能是 null(AI apply_page_filter 推过来"全部时间"语义、
        //   或主页未设置时初始为 null)。DatesPickerV2 内部 dates[0]/dates[1] 没空值检查,
        //   null[0] 直接 TypeError 白屏。这里兜底成"过去 6 个月"默认窗口,组件能正常打开,
        //   用户在弹层里随便选一个新区间即可(2026-04-29 李晓伟反馈"AI 结果出来后点上面白屏")。
        dates={
          filter.times && filter.times.length === 2
            ? filter.times
            : [
                dayjs().subtract(6, 'months').startOf('day'),
                dayjs().endOf('day')
              ]
        }
        onChange={handleDatesChange}
        onCancel={() => toggleFilterModal('time')}
        singleDayOnly={filter.groupByUser}
      />
      <DepartmentMemberPicker
        open={showMemberPickerModal}
        filterObj={filter as any}
        onOk={handleMemberChange}
        onCancel={() => toggleFilterModal('member')}
      />
      <WatermarksPicker
        visible={showWatermarkModal}
        onOK={handleWatermarkChange}
        onCancel={() => toggleFilterModal('watermark')}
        checkedList={filter.watermarks}
        fetchWatermarkList={(params: any) =>
          dispatch(fetchWatermarkList(params) as any)
        }
      />
      <LocationPicker
        visible={showLocationPickerModal}
        groupId={groupId}
        onCancel={() => toggleFilterModal('location')}
        handleCheckLocation={handleLocationChange}
      />
      {photoLabelEnabled && (
        <TagPicker
          visible={showTagPickerModal}
          checkedList={filter.labelIDs || []}
          matchAll={filter.labelMatchAll || false}
          fromPlace="全部照片筛选标签页"
          onCancel={() => toggleFilterModal('tag')}
          onOK={handleTagChange}
          onSearch={() => {
            if (tagTrackEnabled) {
              trackPicspageDialogClick({
                popTitle: 'tag_filter_picker',
                clickItem: 'search',
                fromPage: fromPageForTrack
              })
            }
          }}
        />
      )}
      {/* 管理标签弹窗 — 仅管理员能进入 (master 新增) */}
      {photoLabelEnabled && isAdmin && (
        <PhotoTagManageModal
          visible={tagManageModalVisible}
          source={manageSource}
          onClose={() => setTagManageModalVisible(false)}
          onSaved={reloadTeamTagCount}
        />
      )}

      {/* 团队打标统计弹窗 — 仅管理员能进入 (master 新增) */}
      {photoLabelEnabled && isAdmin && (
        <PhotoTagTeamStatsModal
          visible={teamStatsModalVisible}
          onClose={() => setTeamStatsModalVisible(false)}
        />
      )}

      <BuildingSafetyEntry onTrack={handleSensorOfClickEvent} />
    </div>
  )
}

export default PhotoFilter
